import React from 'react';
import { Typography, IconButton, Collapse } from '@mui/material';
import { Add as AddIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Button } from 'new/elements/Button';
import { ParameterTable, HeaderTable } from './ApiRequestTables';
import { Parameter, Header } from '../hooks/useParameterManager';
import styles from '../ApiRequestModal.module.scss';

interface ParameterSectionProps {
  mode: 'config' | 'test';
  title: string;
  type?: 'path' | 'query' | 'body' | 'headers';
  parameters?: Parameter[];
  headers?: Header[];
  validationErrors: { [id: number]: string };
  expanded: boolean;
  onToggle: () => void;
  onUpdate: (
    id: number,
    field: string,
    value: string | boolean,
    type?: 'path' | 'query' | 'body',
  ) => void;
  onRemove: (id: number, type?: 'path' | 'query' | 'body') => void;
  onAdd: () => void;
}

export const ParameterSection: React.FC<ParameterSectionProps> = ({
  mode,
  title,
  type,
  parameters,
  headers,
  expanded,
  onToggle,
  onUpdate,
  onRemove,
  onAdd,
  validationErrors,
}) => {
  const isHeaders = type === 'headers';

  const handleUpdate = (id: number, field: string, value: string | boolean) => {
    if (isHeaders) {
      onUpdate(id, field, value);
    } else {
      onUpdate(id, field, value, type);
    }
  };

  const handleRemove = (id: number) => {
    if (isHeaders) {
      onRemove(id);
    } else {
      onRemove(id, type);
    }
  };

  return (
    <div>
      <div className={styles.sectionHeader} onClick={onToggle}>
        <IconButton
          size="small"
          className={`${styles.expandIcon} ${expanded ? styles.expanded : ''}`}
        >
          <ExpandMoreIcon />
        </IconButton>
        <Typography variant="caption">{title}</Typography>
      </div>
      <Collapse in={expanded}>
        <div className={styles.sectionContent}>
          <div className={styles.tableWrapper}>
            {isHeaders ? (
              <HeaderTable
                mode={mode}
                headers={headers || []}
                validationErrors={validationErrors}
                onUpdate={handleUpdate}
                onRemove={handleRemove}
              />
            ) : (
              <ParameterTable
                mode={mode}
                parameters={parameters || []}
                type={type as 'path' | 'query' | 'body'}
                title={title}
                validationErrors={validationErrors}
                onUpdate={handleUpdate}
                onRemove={handleRemove}
              />
            )}
          </div>
          {mode === 'config' && (
            <div className={styles.parameterActions}>
              <Button
                variant="text"
                onClick={onAdd}
                className={styles.addParameterButton}
                startIcon={<AddIcon />}
              >
                <Typography variant="caption">
                  Add {isHeaders ? 'Header' : title.replace(' Parameters', ' Parameter')}
                </Typography>
              </Button>
            </div>
          )}
        </div>
      </Collapse>
    </div>
  );
};
